import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { notifyError, notifySuccess } from '../Notification';
import { api } from '../../api/index';
import './pollingEdit.css';

const baseURL = process.env.REACT_APP_API_URL;

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
const getLuminance = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance;
};

/**
 * Get contrasting text color (dark or light) based on background color
 * Uses luminance calculation to ensure readability
 */
const getContrastingTextColor = (backgroundColor, preferredColor = '#000000') => {
  // If preferred color is too similar to background, use contrasting color
  const bgLuminance = getLuminance(backgroundColor);
  const textLuminance = getLuminance(preferredColor);

  // If contrast is poor (difference < 0.3), use contrasting color
  const lumDiff = Math.abs(bgLuminance - textLuminance);

  if (lumDiff < 0.3) {
    // Use dark text on light backgrounds, light text on dark backgrounds
    return bgLuminance > 0.5 ? '#1a1a2e' : '#ffffff';
  }

  return preferredColor;
};

const PollingEdit = ({
  poll,
  deviceId,
  deckId,
  storeId,
  onSaveSuccess,
  viewMode = false
}) => {
  const [pollingConfig, setPollingConfig] = useState({
    title: '',
    fontFamily: 'Arial',
    fontSize: '36px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontColor: '#000000',
    backgroundColor: '#fafafa'
  });

  const [initialState, setInitialState] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  // Poll options state (brand icons - max 6)
  const [pollOptions, setPollOptions] = useState([]);
  const [newIconFiles, setNewIconFiles] = useState([]);
  const [newIconLabels, setNewIconLabels] = useState(['', '', '', '', '', '']);
  const [iconBlobUrls, setIconBlobUrls] = useState([]);

  const fontOptions = [
    'Arial', 'Verdana', 'Georgia', 'Courier New', 'Sofia',
    'Times New Roman', 'Poppins', 'Roboto', 'Open Sans'
  ];

  const backgroundColorOptions = [
    { color: '#fafafa', name: 'Default Light' },
    { color: '#f0f9ff', name: 'Light Blue' },
    { color: '#f0fdf4', name: 'Light Green' },
    { color: '#fefce8', name: 'Light Yellow' },
    { color: '#fdf2f8', name: 'Light Pink' },
    { color: '#f3f4f6', name: 'Light Gray' },
    { color: '#fef9c3', name: 'Light Cream' },
    { color: '#e0f2fe', name: 'Light Cyan' },
    { color: '#ecfdf5', name: 'Light Mint' },
    { color: '#fff7ed', name: 'Light Peach' },
    { color: '#f5f3ff', name: 'Light Lavender' },
    { color: '#f9fafb', name: 'Light Silver' },
    { color: '#1a1a2e', name: 'Dark Navy' },
    { color: '#3a1a40', name: 'Dark Plum' },
    { color: '#1a3a38', name: 'Dark Teal' }
  ];

  // Initialize state from existing poll
  useEffect(() => {
    if (poll) {
      let parsedTextStyle = {};
      if (typeof poll.text_style === 'string') {
        try {
          parsedTextStyle = JSON.parse(poll.text_style);
        } catch (e) {
          console.error("Failed to parse text_style:", e);
        }
      } else if (typeof poll.text_style === 'object' && poll.text_style !== null) {
        parsedTextStyle = poll.text_style;
      }

      const state = {
        title: poll.title || '',
        fontFamily: parsedTextStyle.font_family || 'Arial',
        fontSize: parsedTextStyle.font_size || '36px',
        fontWeight: parsedTextStyle.font_weight || 'normal',
        fontStyle: parsedTextStyle.font_style || 'normal',
        fontColor: parsedTextStyle.color || '#000000',
        backgroundColor: poll.background_color || '#fafafa',
        pollOptions: poll.options && Array.isArray(poll.options) ? poll.options : []
      };

      setPollingConfig(state);
      setInitialState(state);

      // Load existing poll options if available
      if (poll.options && Array.isArray(poll.options)) {
        setPollOptions(poll.options);
      }

      setHasChanges(false);
    }
  }, [poll]);

  // Detect changes
  useEffect(() => {
    // For new polling (no initialState), consider it changed if title or new icons exist
    if (!initialState) {
      const isNewAndHasContent = pollingConfig.title.trim() !== '' || newIconFiles.length > 0;
      setHasChanges(isNewAndHasContent);
      return;
    }

    // For existing polling, check if anything has changed from initial state
    const currentChanges =
      pollingConfig.title !== initialState.title ||
      pollingConfig.fontFamily !== initialState.fontFamily ||
      pollingConfig.fontSize !== initialState.fontSize ||
      pollingConfig.fontWeight !== initialState.fontWeight ||
      pollingConfig.fontStyle !== initialState.fontStyle ||
      pollingConfig.fontColor !== initialState.fontColor ||
      pollingConfig.backgroundColor !== initialState.backgroundColor ||
      newIconFiles.length > 0 ||
      pollOptions.length !== initialState.pollOptions.length;

    setHasChanges(currentChanges);
  }, [pollingConfig, newIconFiles, initialState, pollOptions]);

  // Manage blob URLs for icon previews - revoke old ones and create new ones
  useEffect(() => {
    // Revoke old blob URLs to prevent memory leaks
    iconBlobUrls.forEach(url => URL.revokeObjectURL(url));

    // Create new blob URLs for current files
    const newUrls = newIconFiles.map(file => URL.createObjectURL(file));
    setIconBlobUrls(newUrls);

    // Cleanup: revoke URLs when component unmounts or files change
    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [newIconFiles]);

  // Update CSS variables for live preview
  useEffect(() => {
    document.documentElement.style.setProperty('--polling-bg-color', pollingConfig.backgroundColor);
    document.documentElement.style.setProperty('--polling-color', pollingConfig.fontColor);
    document.documentElement.style.setProperty('--polling-font-family', pollingConfig.fontFamily);
    document.documentElement.style.setProperty('--polling-font-size', pollingConfig.fontSize);
    document.documentElement.style.setProperty('--polling-font-weight', pollingConfig.fontWeight);
    document.documentElement.style.setProperty('--polling-font-style', pollingConfig.fontStyle);
  }, [pollingConfig]);

  const handleConfigChange = (key, value) => {
    setPollingConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleIconUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate max 6 icons
    const currentTotal = pollOptions.length + newIconFiles.length;
    if (currentTotal + files.length > 6) {
      notifyError('Maximum 6 brand icons allowed');
      return;
    }

    // Validate file types
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      notifyError('Only image files are allowed');
    }

    setNewIconFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveNewIcon = (index) => {
    setNewIconFiles(prev => prev.filter((_, i) => i !== index));
    setNewIconLabels(prev => {
      const updated = [...prev];
      updated[index] = '';
      return updated;
    });
  };

  const handleRemoveExistingIcon = async (optionId) => {
    if (!window.confirm('Are you sure you want to delete this icon?')) return;

    try {
      await api.delete(`/polls/poll-options/${optionId}`);
      setPollOptions(prev => prev.filter(opt => opt.option_id !== optionId));
      notifySuccess('Icon deleted successfully');
    } catch (error) {
      console.error('Error deleting icon:', error);
      notifyError('Failed to delete icon');
    }
  };

  const handleLabelChange = (index, value) => {
    const updated = [...newIconLabels];
    updated[index] = value;
    setNewIconLabels(updated);
  };

  const handleSubmit = async () => {
    // Validation
    if (!pollingConfig.title.trim()) {
      notifyError('Please enter a polling question');
      return;
    }

    const totalIcons = pollOptions.length + newIconFiles.length;
    if (totalIcons === 0) {
      notifyError('Please upload at least one brand icon');
      return;
    }

    if (totalIcons > 6) {
      notifyError('Maximum 6 brand icons allowed');
      return;
    }

    // Validate labels for new icons
    for (let i = 0; i < newIconFiles.length; i++) {
      if (!newIconLabels[i] || !newIconLabels[i].trim()) {
        notifyError(`Please enter a label for icon ${i + 1}`);
        return;
      }
    }

    setLoading(true);

    try {
      let pollId = poll?.poll_id;

      // Step 1: Create or update poll
      const textStyle = JSON.stringify({
        font_family: pollingConfig.fontFamily,
        font_size: pollingConfig.fontSize,
        font_weight: pollingConfig.fontWeight,
        font_style: pollingConfig.fontStyle,
        color: pollingConfig.fontColor
      });

      const pollData = {
        deck_id: deckId,
        device_id: deviceId,
        store_id: storeId,
        title: pollingConfig.title,
        text_style: textStyle,
        background_color: pollingConfig.backgroundColor
      };

      if (pollId) {
        // Update existing poll
        await api.put(`/polls/${pollId}`, pollData);
        console.log('Poll updated:', pollId);
      } else {
        // Create new poll
        const response = await api.post('/polls', pollData);
        pollId = response.data.data.poll_id;
        console.log('Poll created:', pollId);
      }

      // Step 2: Upload new icons if any
      if (newIconFiles.length > 0) {
        const formData = new FormData();
        formData.append('poll_id', pollId);

        // Add files
        newIconFiles.forEach((file) => {
          formData.append('icons', file);
        });

        // Add labels as JSON array
        const labels = newIconFiles.map((_, idx) => newIconLabels[idx].trim());
        formData.append('labels', JSON.stringify(labels));

        await api.post('/polls/poll-options', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('Icons uploaded successfully');

        // Step 3: Refetch poll options to show newly uploaded images
        try {
          const optionsResponse = await api.get(`/polls/poll-options/${pollId}`);
          if (optionsResponse.data.data) {
            setPollOptions(optionsResponse.data.data);
            console.log('Poll options updated:', optionsResponse.data.data);
          }
        } catch (err) {
          console.error('Error refetching poll options:', err);
          // Don't show error to user - options should still work
        }
      }

      notifySuccess('Polling configuration saved successfully!');

      // Reset new icons
      setNewIconFiles([]);
      setNewIconLabels(['', '', '', '', '', '']);

      // Callback to parent
      if (onSaveSuccess) {
        onSaveSuccess(pollId);
      }

    } catch (error) {
      console.error('Error saving polling config:', error);
      notifyError(error.response?.data?.message || 'Failed to save polling configuration');
    } finally {
      setLoading(false);
    }
  };

  const getFontSizeLabel = () => {
    const size = parseInt((pollingConfig.fontSize || '36px').replace('px', ''));
    if (size >= 44) return 'Large';
    if (size >= 36) return 'Medium';
    return 'Small';
  };

  const setFontSizeFromLabel = (label) => {
    const labelMap = {
      'Small': '28px',
      'Medium': '36px',
      'Large': '44px'
    };
    handleConfigChange('fontSize', labelMap[label] || '36px');
  };

  return (
    <div className="polling-edit-container">
      <div className="polling-edit-sidebar">
        <div className="polling-section">
          <h3 className="polling-section-title">Polling Question</h3>
          <div className="polling-question-input-container">
            <input
              type="text"
              value={pollingConfig.title}
              onChange={(e) => handleConfigChange('title', e.target.value)}
              className="polling-question-input"
              placeholder="e.g., Which brand do you prefer?"
              disabled={viewMode}
            />
          </div>
        </div>

        <div className="polling-section">
          <h3 className="polling-section-title">Font Size</h3>
          <div className="polling-font-size-buttons">
            {['Small', 'Medium', 'Large'].map(size => (
              <button
                key={size}
                onClick={() => setFontSizeFromLabel(size)}
                className={`polling-font-size-btn ${getFontSizeLabel() === size ? 'polling-font-size-btn-active' : ''}`}
                disabled={viewMode}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div className="polling-font-style-color-container">
          <div className="polling-section">
            <h3 className="polling-section-title">Font Family</h3>
            <select
              value={pollingConfig.fontFamily}
              onChange={(e) => handleConfigChange('fontFamily', e.target.value)}
              className="polling-font-select"
              disabled={viewMode}
            >
              {fontOptions.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div className="polling-section">
            <h3 className="polling-section-title">Font Style</h3>
            <div className="polling-font-style-buttons">
              <button
                onClick={() => handleConfigChange('fontWeight', pollingConfig.fontWeight === 'bold' ? 'normal' : 'bold')}
                className={`polling-style-btn ${pollingConfig.fontWeight === 'bold' ? 'polling-style-btn-active' : ''}`}
                disabled={viewMode}
              >
                <strong>B</strong>
              </button>
              <button
                onClick={() => handleConfigChange('fontStyle', pollingConfig.fontStyle === 'italic' ? 'normal' : 'italic')}
                className={`polling-style-btn ${pollingConfig.fontStyle === 'italic' ? 'polling-style-btn-active' : ''}`}
                disabled={viewMode}
              >
                <em>I</em>
              </button>
            </div>
          </div>

          <div className="polling-section">
            <h3 className="polling-section-title">Font Color</h3>
            <input
              type="color"
              value={pollingConfig.fontColor}
              onChange={(e) => handleConfigChange('fontColor', e.target.value)}
              className="polling-color-input"
              disabled={viewMode}
            />
          </div>
        </div>

        <div className="polling-section">
          <h3 className="polling-section-title">Background Color</h3>
          <div className="polling-background-color-grid">
            {backgroundColorOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleConfigChange('backgroundColor', option.color)}
                className={`polling-background-color-option ${pollingConfig.backgroundColor === option.color ? 'polling-background-color-active' : ''}`}
                disabled={viewMode}
                title={option.name}
              >
                <div
                  className="polling-background-color-preview"
                  style={{ backgroundColor: option.color }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Brand Icons Upload */}
        <div className="polling-section">
          <h3 className="polling-section-title">
            Brand Icons ({pollOptions.length + newIconFiles.length}/6)
          </h3>

          {/* Existing Icons */}
          {pollOptions.length > 0 && (
            <div className="polling-existing-icons">
              <h4 className="polling-subsection-title">Current Icons</h4>
              <div className="polling-icons-grid">
                {pollOptions.map((option) => (
                  <div key={option.option_id} className="polling-icon-card">
                    <img src={option.icon_url} alt={option.label} className="polling-icon-preview" />
                    <span className="polling-icon-label">{option.label}</span>
                    {!viewMode && (
                      <button
                        className="polling-icon-delete-btn"
                        onClick={() => handleRemoveExistingIcon(option.option_id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Icons Preview */}
          {newIconFiles.length > 0 && (
            <div className="polling-new-icons">
              <h4 className="polling-subsection-title">New Icons to Upload</h4>
              <div className="polling-icons-grid">
                {newIconFiles.map((file, index) => (
                  <div key={index} className="polling-icon-card">
                    <img
                      src={iconBlobUrls[index]}
                      alt={`New icon ${index + 1}`}
                      className="polling-icon-preview"
                    />
                    <input
                      type="text"
                      value={newIconLabels[index]}
                      onChange={(e) => handleLabelChange(index, e.target.value)}
                      placeholder="Brand name"
                      className="polling-icon-label-input"
                    />
                    <button
                      className="polling-icon-delete-btn"
                      onClick={() => handleRemoveNewIcon(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {!viewMode && (pollOptions.length + newIconFiles.length < 6) && (
            <div className="polling-upload-section">
              <label htmlFor="icon-upload" className="polling-upload-btn">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Upload Brand Icons
              </label>
              <input
                id="icon-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleIconUpload}
                style={{ display: 'none' }}
              />
              <p className="polling-upload-hint">
                Upload {6 - (pollOptions.length + newIconFiles.length)} more icon(s). PNG, JPG, or SVG.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!viewMode && (
          <div className="polling-action-buttons">
            <button
              onClick={handleSubmit}
              className="polling-btn polling-btn-primary"
              disabled={loading || !pollingConfig.title.trim() || (pollOptions.length === 0 && newIconFiles.length === 0) || (poll && !hasChanges)}
            >
              {loading ? 'Saving...' : poll ? 'Update Polling' : 'Create Polling'}
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="polling-btn polling-btn-secondary"
            >
              Preview
            </button>
          </div>
        )}
      </div>

      {/* Preview Panel */}
      <div className="polling-preview">
        <div
          className="polling-preview-content"
          style={{ backgroundColor: pollingConfig.backgroundColor }}
        >
          <div className="polling-preview-title">
            <div
              style={{
                fontFamily: pollingConfig.fontFamily,
                fontSize: pollingConfig.fontSize,
                fontWeight: pollingConfig.fontWeight,
                fontStyle: pollingConfig.fontStyle,
                color: pollingConfig.fontColor,
                textAlign: 'center',
                marginBottom: '40px'
              }}
            >
              {pollingConfig.title || 'Your polling question will appear here'}
            </div>
          </div>

          <div className="polling-options-container">
            <div className="polling-options-grid">
              {/* Show existing icons */}
              {pollOptions.map((option) => (
                <button key={option.option_id} className="polling-option-btn" disabled>
                  <div className="polling-option-icon">
                    <img src={option.icon_url} alt={option.label} />
                  </div>
                  <span
                    className="polling-option-label"
                    style={{ color: getContrastingTextColor(pollingConfig.backgroundColor, pollingConfig.fontColor) }}
                  >
                    {option.label}
                  </span>
                </button>
              ))}

              {/* Show new icon previews */}
              {newIconFiles.map((file, index) => (
                <button key={`new-${index}`} className="polling-option-btn" disabled>
                  <div className="polling-option-icon">
                    <img
                      src={iconBlobUrls[index]}
                      alt={newIconLabels[index] || `Icon ${index + 1}`}
                    />
                  </div>
                  <span
                    className="polling-option-label"
                    style={{ color: getContrastingTextColor(pollingConfig.backgroundColor, pollingConfig.fontColor) }}
                  >
                    {newIconLabels[index] || `Brand ${index + 1}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        centered
        size="lg"
        className="polling-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Polling Slide Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0', background: pollingConfig.backgroundColor }}>
          <div
            style={{
              width: '100%',
              minHeight: '400px',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 30px',
              backgroundColor: pollingConfig.backgroundColor
            }}
          >
            <div style={{ width: '100%', marginBottom: '40px' }}>
              <div
                style={{
                  fontFamily: pollingConfig.fontFamily,
                  fontSize: pollingConfig.fontSize,
                  fontWeight: pollingConfig.fontWeight,
                  fontStyle: pollingConfig.fontStyle,
                  color: pollingConfig.fontColor,
                  textAlign: 'center'
                }}
              >
                {pollingConfig.title || 'Your polling question will appear here'}
              </div>
            </div>

            <div style={{ width: '100%', maxWidth: '600px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '24px'
                }}
              >
                {/* Show existing icons */}
                {pollOptions.map((option) => (
                  <button
                    key={option.option_id}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '0',
                      padding: '0',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.3s'
                    }}
                    disabled
                  >
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        border: '4px solid #dc2626',
                        background: 'rgba(255, 255, 255, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}
                    >
                      <img
                        src={option.icon_url}
                        alt={option.label}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '50%'
                        }}
                      />
                    </div>
                    <span style={{
                      fontSize: '0.9em',
                      fontWeight: '600',
                      color: getContrastingTextColor(pollingConfig.backgroundColor, pollingConfig.fontColor),
                      textAlign: 'center'
                    }}>
                      {option.label}
                    </span>
                  </button>
                ))}

                {/* Show new icon previews */}
                {newIconFiles.map((file, index) => (
                  <button
                    key={`new-${index}`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '0',
                      padding: '0',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.3s'
                    }}
                    disabled
                  >
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        border: '4px solid #dc2626',
                        background: 'rgba(255, 255, 255, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}
                    >
                      <img
                        src={iconBlobUrls[index]}
                        alt={newIconLabels[index] || `Icon ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '50%'
                        }}
                      />
                    </div>
                    <span style={{
                      fontSize: '0.9em',
                      fontWeight: '600',
                      color: getContrastingTextColor(pollingConfig.backgroundColor, pollingConfig.fontColor),
                      textAlign: 'center'
                    }}>
                      {newIconLabels[index] || `Brand ${index + 1}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowPreview(false)}
          >
            Close Preview
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PollingEdit;
